import { useAuthStore } from '@/features/auth/stores/auth.store';
import { PERMISSION_KEYS } from '@/features/conversation/models/permission';
import { Authorize } from '@/shared/components/authorize';
import { Delete, Replay } from '@mui/icons-material';
import { CircularProgress, ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { Message } from '../../models/message';
import type { MessageDeleteReq } from '../../models/message-action-dto';
import { useMessageDelete } from '../../queries/use-message-delete';

const RECALL_WINDOW_MINUTES = 15;

interface MesageMenuDeleteItemProps {
  isOwn: boolean;
  msg: Message;
  scopeChoice?: MessageDeleteReq['scope'];
  onClose: () => void;
}

export const MesageMenuDeleteItem: FC<MesageMenuDeleteItemProps> = ({ isOwn, msg, onClose, scopeChoice = 'Me' }) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { xoa, isDeleting } = useMessageDelete();

  const handleDelete = async () => {
    if (!msg.conversationId || !user) return;
    try {
      await xoa({
        messageId: msg.messageId,
        conversationId: msg.conversationId,
        bucketId: msg.bucketId,
        scope: scopeChoice
      });
    } finally {
      onClose();
    }
  };

  const withinRecallWindow = dayjs(msg.sentAt).isAfter(dayjs().subtract(RECALL_WINDOW_MINUTES, 'minute'));

  if (isOwn && withinRecallWindow) {
    return undefined;
  }

  const requiredPermission = isOwn ? PERMISSION_KEYS.MESSAGE_DELETE_OWN : PERMISSION_KEYS.MESSAGE_DELETE_ANY;

  return (
    <Authorize conversationId={msg.conversationId} permissions={[requiredPermission]}>
      <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }} disabled={isDeleting}>
        <ListItemIcon>
          {isDeleting ? <CircularProgress size={20} /> : scopeChoice === 'Me' ? <Delete /> : <Replay />}
        </ListItemIcon>
        <ListItemText>
          {scopeChoice === 'Me' ? t('messages.delete.menuItemForMe') : t('messages.actions.recall')}
        </ListItemText>
      </MenuItem>
    </Authorize>
  );
};
